export type RestoreHandler = (payload: unknown) => void;

let handler: RestoreHandler | null = null;
const pending: unknown[] = [];

const SHELL_RUN_KEY = 'skuulbe_shell_run';
const SHELL_SESSION_KEYS = ['token', 'user', 'tokenExpiry', 'nextAction'];

// The native shell appends "SkuulbeShell/1 run/<id>" to its user agent, with a
// new id each time the app process starts. Older shells send nothing, and get
// neither the purge nor the bridge.
const readShellRunId = (): string | null => {
  if (typeof navigator === 'undefined') return null;
  const match = /\bSkuulbeShell\/1 run\/([a-z0-9]+)\b/i.exec(
    navigator.userAgent ?? ''
  );
  return match ? match[1] : null;
};

const shellRunId = readShellRunId();

export const postToNativeShell = (message: Record<string, unknown>) => {
  if (typeof window !== 'undefined' && window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify(message));
  }
};

const rememberShellRun = (runId: string) => {
  try {
    localStorage.setItem(SHELL_RUN_KEY, runId);
  } catch {
    // Every load is then treated as a new run.
  }
};

// Android's incognito WebView keeps DOM storage across app launches, so the
// page's own saved login would skip the native unlock. Drop it on the first
// load of each app run; reloads within the run keep it. The selected child is
// kept: a restore proves it is the same parent. Must run before UserProvider
// reads localStorage.
const dropSessionOnNewShellRun = (runId: string) => {
  let newRun = true;
  try {
    newRun = localStorage.getItem(SHELL_RUN_KEY) !== runId;
  } catch {
    // Can't tell a reload from a new run: fail closed.
  }
  if (!newRun) return;
  let purged = true;
  SHELL_SESSION_KEYS.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch {
      purged = false;
    }
  });
  // An incomplete purge leaves the run unrecorded so the next load retries.
  if (purged) rememberShellRun(runId);
};

// Call after clearing localStorage on any sign-out: the shell drops its saved
// session, and the run id is written back so a fresh login in this run
// survives a reload.
export const signalShellSignedOut = () => {
  postToNativeShell({ type: 'REMOVE_TOKEN' });
  if (shellRunId) rememberShellRun(shellRunId);
};

// Installed at import (before React renders) so a restore injected by the
// native shell while the app is still booting is queued rather than lost.
if (typeof window !== 'undefined' && shellRunId) {
  dropSessionOnNewShellRun(shellRunId);
  window.__skuulbeNativeBridge = {
    restore(payload: unknown) {
      if (handler) {
        handler(payload);
      } else {
        pending.push(payload);
      }
    },
  };
}

export const attachRestoreHandler = (fn: RestoreHandler): (() => void) => {
  handler = fn;
  while (pending.length > 0 && handler === fn) {
    fn(pending.shift());
  }
  return () => {
    if (handler === fn) handler = null;
  };
};
